import { useLocation, useNavigate, generatePath } from 'react-router';
import { useEffect, useMemo } from 'react';

import {
  Table,
  LoadingSpinner,
  Typography,
  useWindowSize,
  Chip,
} from '@data-products-and-ai/react-components';
import { TableRowData } from '@data-products-and-ai/react-components/lib/components/DataDisplay/Table/types';

import { Pagination, SemiCircleProgressBar } from '@/components';
import {
  OutagesFilters,
  OutagesList,
  RiskWarning,
} from '@/pages/Outages/components';
import {
  DESKTOP_LARGE_WIDTH,
  INITIAL_PAGE,
  INITIAL_PAGE_SIZE,
  INITIAL_SORT_DIRECTION,
  SPINNER_SIZE,
  TABLET_WIDTH,
} from '@/constants';
import {
  formatDateString,
  getRiskColor,
  getRiskLabel,
  readSessionStorageJson,
  toPascalCase,
  writeSessionStorageJson,
} from '@/utils';
import { OutagesFiltersI } from '@/types';
import { usePagination, useTableSort } from '@/hooks';
import { useGetOutagesQuery } from '@/apis/outages/outagesApi';
import {
  useOutagesFilters,
  useOutagesTableHeaders,
} from '@/pages/Outages/hooks';
import { OUTAGE_ROUTE } from '@/pages/Outages/routes';
import { Outage } from '@/apis/outages/types/outagesApi';

import '@/pages/Outages/styles.scss';

interface PersistedOutagesPageState {
  filters?: Partial<OutagesFiltersI>;
  sortKey?: string | null;
  sortDir?: string;
}

const OUTAGES_PAGE_STORAGE_KEY = 'outages-page-filters';

const OUTAGES_SORT_KEYS: Array<keyof Outage> = [
  'outageStart',
  'outageNormalized0_100',
];

const parsePersistedOutagesPageState = (value: unknown) => {
  if (!value || typeof value !== 'object') return null;

  const parsed = value as PersistedOutagesPageState;
  const rawFilters = parsed.filters && typeof parsed.filters === 'object'
    ? (parsed.filters as Partial<OutagesFiltersI>)
    : undefined;

  const filters: Partial<OutagesFiltersI> | undefined = rawFilters
    ? {
        siteId:
          typeof rawFilters.siteId === 'string' ? rawFilters.siteId : null,
        startDate:
          typeof rawFilters.startDate === 'string'
            ? rawFilters.startDate
            : null,
        endDate:
          typeof rawFilters.endDate === 'string' ? rawFilters.endDate : null,
        query: typeof rawFilters.query === 'string' ? rawFilters.query : '',
      }
    : undefined;

  const sortKey = OUTAGES_SORT_KEYS.includes(parsed.sortKey as keyof Outage)
    ? (parsed.sortKey as keyof Outage)
    : undefined;
  const sortDir =
    parsed.sortDir === 'asc' || parsed.sortDir === 'desc'
      ? parsed.sortDir
      : undefined;

  return {
    filters,
    sortKey,
    sortDir,
  };
};

export const OutagesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { width } = useWindowSize();
  const isSmallerScreen = width <= TABLET_WIDTH;
  const backUrl = `${location.pathname}${location.search}`;

  const persistedState = useMemo(
    () => readSessionStorageJson(OUTAGES_PAGE_STORAGE_KEY, parsePersistedOutagesPageState),
    [],
  );
  const { outagesFilters, updateFilters } = useOutagesFilters({
    initialFilters: persistedState?.filters,
  });
  const { currentPage, pageSize, setPage } = usePagination({
    initialPage: INITIAL_PAGE,
    initialPageSize: INITIAL_PAGE_SIZE,
  });
  const { sortConfig, requestSort } = useTableSort<Outage>(
    (persistedState?.sortKey ?? 'outageStart') as keyof Outage,
    (persistedState?.sortDir ?? INITIAL_SORT_DIRECTION) as
      | 'asc'
      | 'desc',
  );
  const orderBy = toPascalCase(sortConfig.key?.toString() ?? '');

  const { data, isLoading, isFetching } = useGetOutagesQuery({
    search: outagesFilters.query,
    endDate: outagesFilters.endDate,
    startDate: outagesFilters.startDate,
    siteId: Number(outagesFilters.siteId),
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    sortDir: sortConfig.direction,
    orderBy,
  });
  const outages = data?.results ?? [];
  const totalItems = data?.total ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  const headers = useOutagesTableHeaders(
    width <= DESKTOP_LARGE_WIDTH,
    sortConfig,
    requestSort,
  );

  const rows: TableRowData[] = useMemo(
    () =>
      (outages ?? []).map((outage) => ({
        id: outage.outageId.toString(),
        isClickable: true,
        data: {
          ...outage,
          outageStart: formatDateString(outage.outageStart, 'MM/DD/YYYY'),
          outageEnd: formatDateString(outage.outageEnd, 'MM/DD/YYYY'),
          workerCount: (
            <Chip
              label={outage.workerCount.toString()}
              chipBorderType="round"
              color="primary"
              chipType="thin"
              size="extra-small"
            />
          ),
          outageNormalized0_100: (
            <div className="outages-page__risk-indicator">
              <SemiCircleProgressBar
                progress={outage.outagePercentile}
                size={100}
                strokeWidth={8}
                color={getRiskColor(outage.outagePercentile)}
                labelText={getRiskLabel(outage.outagePercentile)}
                showLabel
                mode="label"
              />
              <RiskWarning
                highRiskCount={outage.highRiskCount ?? 0}
                midRiskCount={outage.midRiskCount ?? 0}
              />
            </div>
          ),
        },
      })),
    [outages],
  );

  const handleFiltersUpdate = (filters: Partial<OutagesFiltersI>) => {
    updateFilters(filters);
    setPage(INITIAL_PAGE);
  };

  const handleRowClick = (row: TableRowData) => {
    if (!row.id) return;

    navigate(generatePath(OUTAGE_ROUTE, { outageId: row.id }), {
      state: { from: backUrl },
    });
  };

  const handlePageChange = (page: number) => {
    setPage(page);

    if (isSmallerScreen) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    writeSessionStorageJson(OUTAGES_PAGE_STORAGE_KEY, {
      filters: outagesFilters,
      sortKey: sortConfig.key,
      sortDir: sortConfig.direction,
    });
  }, [outagesFilters, sortConfig.key, sortConfig.direction]);

  if (isLoading)
    return (
      <div className="outages-page-spinner">
        <LoadingSpinner
          text="Loading outages..."
          tag="textdefault"
          size={SPINNER_SIZE}
          color="var(--color-primary)"
          align="center"
          inline
        />
      </div>
    );

  if (!data) return null;

  return (
    <div className="outages-page">
      <div className="outages-page__title">
        <Typography
          color="var(--color-black)"
          tag={isSmallerScreen ? 'h3' : 'h2'}
        >
          Active Outages
        </Typography>
        <Typography color="var(--color-default)" tag="textdefault">
          {data?.total ?? 0}
        </Typography>
      </div>

      <OutagesFilters
        disabled={isFetching}
        outagesFilters={outagesFilters}
        onFiltersUpdate={handleFiltersUpdate}
      />

      <div className="outages-page__data">
        {isFetching && (
          <div className="outages-page__data-fetching">
            <LoadingSpinner
              text="Loading outages..."
              tag="textdefault"
              size={SPINNER_SIZE}
              color="var(--color-primary)"
              align="center"
              inline
            />
          </div>
        )}
        {isSmallerScreen ? (
          <OutagesList outages={outages ?? []} />
        ) : (
          <Table
            rows={rows}
            headers={headers}
            hasBottomBorder
            noResultsMessage="No outages found"
            pointerCursor
            onRowClick={handleRowClick}
            width="100%"
          />
        )}
      </div>

      <div className="outages-page__pagination">
        <Pagination
          disabled={isFetching}
          currentPage={currentPage}
          totalPages={totalPages}
          maxVisiblePages={isSmallerScreen ? 3 : 5}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};
